import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgeSection() {
  const [form, setForm] = useState({
    artist_name: "",
    email: "",
    vibe: "",
    bpm_requested: "",
    genre_requested: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.artist_name || !form.email || !form.vibe) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      const brief = {
        artist_name: form.artist_name,
        email: form.email,
        vibe: form.vibe,
        bpm_requested: form.bpm_requested ? parseInt(form.bpm_requested) : null,
        genre_requested: form.genre_requested || null,
        notes: form.notes || null,
      };

      const res = await axios.post(`${API}/forge/create-session`, {
        brief,
        origin_url: window.location.origin,
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error("Forge error:", err);
      toast.error("Failed to create order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="forge"
      data-testid="forge-section"
      className="py-24 px-6 relative overflow-hidden"
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1655931546508-9f948f52e079?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbXVzaWMlMjBzdHVkaW8lMjBwcm9kdWNlcnxlbnwwfHx8fDE3NzYxNzI1Mjh8MA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/70" />

      <div className="relative z-10 max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
        {/* Left: Copy */}
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-gold mb-3">The Forge</p>
          <h2
            className="text-4xl sm:text-5xl tracking-tight font-medium text-[#EAEAEA] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Commission Your Sound
          </h2>
          <p className="text-[#A1A1A1] text-base leading-relaxed mb-4">
            Need something unique? Describe your vision and our producers will craft
            a custom beat exclusively for you. Full stems, exclusive rights, delivered in 24 hours.
          </p>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-4xl font-semibold text-gold">$99</span>
            <span className="text-sm text-[#737373]">flat rate</span>
          </div>
          <p className="text-xs text-[#737373]">
            Includes WAV + Stems + Exclusive Forge License
          </p>
        </div>

        {/* Right: Form */}
        <form
          data-testid="forge-form"
          onSubmit={handleSubmit}
          className="space-y-5 chrome-panel backdrop-blur-md p-8 rounded-2xl"
        >
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-2 block">
              Artist Name *
            </label>
            <Input
              data-testid="forge-artist-name"
              value={form.artist_name}
              onChange={(e) => update("artist_name", e.target.value)}
              placeholder="Your artist name"
              data-ph-no-capture
              className="ph-no-capture bg-transparent border-0 border-b border-[#1A1A1A] rounded-none px-0 text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold"
            />
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-2 block">
              Email *
            </label>
            <Input
              data-testid="forge-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="your@email.com"
              data-ph-no-capture
              className="ph-no-capture bg-transparent border-0 border-b border-[#1A1A1A] rounded-none px-0 text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold"
            />
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-2 block">
              Describe Your Vibe *
            </label>
            <Textarea
              data-testid="forge-vibe"
              value={form.vibe}
              onChange={(e) => update("vibe", e.target.value)}
              placeholder='E.g., "Dark, cinematic trap with heavy 808s, haunting melodies, and a Travis Scott vibe"'
              rows={3}
              data-ph-no-capture
              className="ph-no-capture bg-transparent border border-[#1A1A1A] rounded-sm text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-2 block">
                BPM (optional)
              </label>
              <Input
                data-testid="forge-bpm"
                type="number"
                value={form.bpm_requested}
                onChange={(e) => update("bpm_requested", e.target.value)}
                placeholder="140"
                className="bg-transparent border-0 border-b border-[#1A1A1A] rounded-none px-0 text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-2 block">
                Genre (optional)
              </label>
              <Input
                data-testid="forge-genre"
                value={form.genre_requested}
                onChange={(e) => update("genre_requested", e.target.value)}
                placeholder="Trap, Drill..."
                className="bg-transparent border-0 border-b border-[#1A1A1A] rounded-none px-0 text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold"
              />
            </div>
          </div>

          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-[#737373] mb-2 block">
              Additional Notes
            </label>
            <Textarea
              data-testid="forge-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Any reference tracks, specific instruments, or special requests..."
              rows={2}
              data-ph-no-capture
              className="ph-no-capture bg-transparent border border-[#1A1A1A] rounded-sm text-[#EAEAEA] placeholder:text-[#737373] focus-visible:ring-0 focus-visible:border-gold resize-none"
            />
          </div>

          <button
            data-testid="forge-submit-btn"
            type="submit"
            disabled={submitting}
            className="metal-gold-surface w-full py-3.5 font-semibold text-sm tracking-widest uppercase rounded-full transition-all hover:brightness-105 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating Order...
              </>
            ) : (
              "Ignite The Forge"
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
